import { useCallback, useRef } from 'react';
import Glint, { glintPosition } from 'components/EmptyState/Glint';
import {
  glintWindow,
  useAnimationFrame,
  usePrefersReducedMotion,
} from 'components/EmptyState/glyphMotion';

/** One beat (ms), the jump of a hand (degrees), and how long the click lasts (in beats). */
const TICK = 1000;
const STEP = 30;
const SNAP = 0.16;
/**
 * One turn of the minute hand, and the glint that follows its return to noon: it lands there on
 * the click of the eleventh beat and stays until the twelfth, the time of the glint.
 */
const TURN = 12;
const GLINT = glintWindow((TURN - 1 + SNAP) / TURN, TURN * TICK);

/** From 0 to 1, slightly past before settling: the click of a hand landing. */
const snap = (p) => {
  const pull = 1.70158;
  return 1 + (pull + 1) * (p - 1) ** 3 + pull * (p - 1) ** 2;
};

/** Jumps done at `t` (in beats) by a hand that jumps every `every` beats, each at the start of its period. */
const ticked = (t, every) => {
  const done = Math.floor(t / every);
  const since = t - done * every;
  return done + snap(Math.min(since / SNAP, 1));
};

/**
 * The Lucide clock ticking in the badge of an empty state (time sheets), ported from the temp
 * worker app (`ClockGlyph` in jobbiz_app_v2): the minute hand jumps an hour per beat and clicks
 * into place, the hour hand jumps once per minute-hand turn, and the glint sweeps the badge each
 * time the minute hand is back on noon. With "reduce motion" the hands stay still, without glint.
 *
 * Must sit in a `relative rounded-full` badge: the glint fills it, drawn as a disc so it needs no
 * clipping.
 */
const ClockGlyph = ({ size = 44, strokeWidth = 2 }) => {
  const reducedMotion = usePrefersReducedMotion();
  const gradientRef = useRef(null);
  const minuteRef = useRef(null);
  const hourRef = useRef(null);

  const render = useCallback((elapsed) => {
    const beats = elapsed / TICK;
    const glint = glintPosition((beats / TURN) % 1, GLINT);
    gradientRef.current?.setAttribute('x1', glint.x1);
    gradientRef.current?.setAttribute('x2', glint.x2);
    minuteRef.current?.setAttribute(
      'transform',
      `rotate(${STEP * ticked(beats, 1)} 12 12)`,
    );
    // The hour hand jumps when the minute hand reaches noon (12th beat), not on the 1st.
    hourRef.current?.setAttribute(
      'transform',
      `rotate(${STEP * (ticked(beats + 1, TURN) - 1)} 12 12)`,
    );
    return true;
  }, []);

  useAnimationFrame(render, !reducedMotion);

  return (
    <>
      {reducedMotion ? null : <Glint ref={gradientRef} />}
      <svg
        className="relative overflow-visible"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        aria-hidden="true"
      >
        <circle cx={12} cy={12} r={10} />
        <path ref={hourRef} d="M12 12l4 2" />
        <path ref={minuteRef} d="M12 12V6" />
      </svg>
    </>
  );
};

export default ClockGlyph;
